import React, { useMemo, useState } from "react";
import { Camera, Columns2, Loader2, MessageSquareWarning, Check, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { formatDateTime } from "@/lib/format";
import { EVIDENCE_TYPES } from "@/lib/wo";
import { StatusChip } from "@/components/common/StatusChip";
import { Section } from "@/features/workorders/Section";
import { PhotoViewer } from "@/features/workorders/PhotoViewer";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export const EvidencePanel = ({ wo, evidencia, observaciones, canUpload, canObserve, onDone }) => {
  const { t } = useT();
  const [tipo, setTipo] = useState("antes");
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [compare, setCompare] = useState(false);
  const [observing, setObserving] = useState(null);
  const [texto, setTexto] = useState("");
  const [busy, setBusy] = useState(false);
  const [broken, setBroken] = useState(() => new Set());
  const markBroken = (id) => setBroken((s) => new Set(s).add(id));

  const grupos = useMemo(() => {
    const map = new Map();
    evidencia.forEach((e) => {
      const key = `${e.ciclo}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    });
    return [...map.entries()].sort((a, b) => Number(a[0]) - Number(b[0]));
  }, [evidencia]);

  const obsOf = (id) => observaciones.filter((o) => o.evidence_id === id);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("tipo", tipo);
      await api.post(`/work-orders/${wo.id}/evidence`, form);
      toast.success(t("app.saved"));
      onDone?.();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setUploading(false);
    }
  };

  const addObservation = async () => {
    setBusy(true);
    try {
      await api.post(`/work-orders/${wo.id}/observations`, { evidence_id: observing.id, texto });
      setObserving(null);
      setTexto("");
      toast.success(t("app.saved"));
      onDone?.();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setBusy(false);
    }
  };

  const resolveObs = async (id) => {
    try {
      await api.post(`/wo-observations/${id}/resolve`, {});
      onDone?.();
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    }
  };

  return (
    <>
      <Section
        title={t("wo.evidence.title")}
        testId="wo-evidence"
        action={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" data-testid="wo-evidence-compare" disabled={!evidencia.length}
              onClick={() => { setCompare(true); setViewer(0); }} className="h-7 px-2 text-[12px]">
              <Columns2 className="mr-1 h-3.5 w-3.5" /> {t("wo.evidence.compare")}
            </Button>
            {canUpload && (
              <>
                <Select value={tipo} onValueChange={setTipo}>
                  <SelectTrigger data-testid="wo-evidence-type" className="h-7 w-[130px] text-[12px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EVIDENCE_TYPES.map((x) => (
                      <SelectItem key={x} value={x}>{t(`wo.evidence.types.${x}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <label className="inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-control bg-primary px-2.5 text-[12px] font-medium text-primary-foreground hover:bg-primary/90">
                  {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                  {t("wo.evidence.upload")}
                  <input type="file" accept="image/*,.heic,.heif" data-testid="wo-evidence-input" className="hidden"
                    onChange={(e) => upload(e.target.files?.[0])} />
                </label>
              </>
            )}
          </div>
        }
      >
        {!evidencia.length ? (
          <p className="py-8 text-center text-[12.5px] text-ink-tertiary">{t("wo.evidence.empty")}</p>
        ) : (
          <div className="space-y-4">
            {grupos.map(([ciclo, fotos]) => (
              <div key={ciclo} data-testid={`wo-evidence-cycle-${ciclo}`}>
                <p className="pb-2 font-mono text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">
                  {t("wo.evidence.cycle")} {ciclo}
                </p>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                  {fotos.map((e) => {
                    const flags = obsOf(e.id);
                    const idx = evidencia.findIndex((x) => x.id === e.id);
                    return (
                      <figure key={e.id} className="group relative">
                        <button
                          data-testid={`wo-photo-${e.id}`}
                          onClick={() => { setCompare(false); setViewer(idx); }}
                          className="block w-full overflow-hidden border border-line"
                        >
                          {broken.has(e.id) ? (
                            <div data-testid={`wo-photo-unavailable-${e.id}`}
                              className="flex aspect-square w-full flex-col items-center justify-center gap-1 bg-surface-sunken px-2 text-center">
                              <ImageOff className="h-5 w-5 text-ink-tertiary" />
                              <p className="text-[10px] leading-tight text-ink-tertiary">{t("wo.evidence.unavailable")}</p>
                              <p className="text-[9.5px] leading-tight text-ink-tertiary">
                                {formatDateTime(e.created_at)}{e.uploaded_by_nombre ? ` · ${e.uploaded_by_nombre}` : ""}
                              </p>
                            </div>
                          ) : (
                            <img src={e.thumb_url || e.url} alt={e.tipo} onError={() => markBroken(e.id)}
                              className="aspect-square w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                          )}
                        </button>
                        <figcaption className="absolute left-1.5 top-1.5">
                          <span
                            className="whitespace-nowrap px-1.5 py-[2px] font-mono text-[10px] uppercase tracking-wide text-paper"
                            style={{ backgroundColor: "var(--ink-overlay)" }}
                          >
                            {t(`wo.evidence.types.${e.tipo}`)}
                          </span>
                        </figcaption>
                        {!!flags.length && (
                          <span data-testid={`wo-photo-flagged-${e.id}`}
                            className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-state-critical text-[10px] font-bold text-paper">
                            {flags.length}
                          </span>
                        )}
                        {canObserve && (
                          <button data-testid={`wo-photo-observe-${e.id}`}
                            onClick={() => { setObserving(e); setTexto(""); }}
                            className="absolute bottom-1.5 right-1.5 hidden bg-surface px-1.5 py-[3px] text-[10.5px] font-medium text-state-critical shadow-sm group-hover:block">
                            {t("wo.evidence.observe")}
                          </button>
                        )}
                      </figure>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 border-t border-line pt-3" data-testid="wo-observations">
          <h3 className="flex items-center gap-1.5 pb-2 text-[12.5px] font-semibold text-ink">
            <MessageSquareWarning className="h-3.5 w-3.5 text-state-critical" />
            {t("wo.evidence.observations")}
          </h3>
          {!observaciones.length ? (
            <p className="text-[12px] text-ink-tertiary">{t("wo.evidence.no_observations")}</p>
          ) : (
            <ul className="space-y-2">
              {observaciones.map((o) => (
                <li key={o.id} data-testid={`wo-observation-${o.id}`} className="flex items-start gap-2.5 border-l-2 border-line-strong bg-surface-sunken px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-[12.5px] leading-snug text-ink">{o.texto}</p>
                    <p className="mt-1 text-[11px] text-ink-tertiary">
                      {o.created_by_nombre} · {formatDateTime(o.created_at)} · {t("wo.evidence.cycle")} {o.ciclo}
                      {o.resolved_at ? ` · ${formatDateTime(o.resolved_at)}` : ""}
                    </p>
                  </div>
                  <StatusChip tone={o.estado === "abierta" ? "red" : "green"}
                    label={o.estado === "abierta" ? t("wo.evidence.open") : t("wo.evidence.resolved")}
                    testId={`wo-observation-state-${o.id}`} />
                  {o.estado === "abierta" && canObserve && (
                    <Button size="sm" variant="ghost" data-testid={`wo-observation-resolve-${o.id}`}
                      onClick={() => resolveObs(o.id)} className="h-6 px-1.5 text-[11.5px] text-state-ok">
                      <Check className="mr-1 h-3 w-3" /> {t("wo.evidence.resolve")}
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </Section>

      {viewer !== null && (
        <PhotoViewer photos={evidencia} index={viewer} compare={compare}
          onToggleCompare={() => setCompare((c) => !c)} onIndexChange={setViewer}
          onClose={() => { setViewer(null); setCompare(false); }} />
      )}

      <Dialog open={!!observing} onOpenChange={(v) => !v && setObserving(null)}>
        <DialogContent data-testid="wo-observation-dialog" className="max-w-[480px] rounded-surface">
          <DialogHeader>
            <DialogTitle className="text-[16px]">{t("wo.evidence.observe")}</DialogTitle>
          </DialogHeader>
          {observing && (
            <img src={observing.thumb_url || observing.url} alt="" className="max-h-[220px] w-full object-cover"
              onError={(ev) => { ev.currentTarget.style.display = "none"; }} />
          )}
          <Textarea data-testid="wo-observation-text" value={texto} onChange={(e) => setTexto(e.target.value)}
            rows={3} placeholder={t("wo.evidence.observation_ph")} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setObserving(null)} className="h-9">
              {t("app.cancel")}
            </Button>
            <Button data-testid="wo-observation-submit" disabled={busy || texto.trim().length < 3} onClick={addObservation} className="h-9">
              {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
              {t("app.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
